import React, { useState, useEffect, useMemo } from 'react'
import { Button, Popover } from '@mui/material'
import Calendar from 'react-calendar'
import dayjs from 'dayjs'
import 'react-calendar/dist/Calendar.css'
import { DayJsDateSelectorCalendarStyleContainer } from './styledComponents'

const DayjsDatePicker = ({
  date: dayjsDate,
  isDisabled = false,
  onChange,
  format = 'DD-MMM-YYYY',
  placeholder = 'Select a date...',
  extraHandlingAfterOnClick = () => {},
  buttonProps = {},
  ButtonComponent = Button,
  calendarProps = {},
  buttonTextOverride = null, // Custom button text override
  offDayWeekdays = [], // weekday indices (0=Sun…6=Sat) to flag red, e.g. practitioner off days
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [calendarView, setCalendarView] = useState('month')
  const offDaySet = useMemo(() => new Set(offDayWeekdays || []), [offDayWeekdays])

  // Open the popover by setting the anchor element (the button)
  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget)
    // When date is unset, start from year view (which shows months) instead of month view (which shows days)
    if (!dayjsDate) {
      setCalendarView('year')
    } else {
      setCalendarView('month') // Use month view when date is set
    }
    extraHandlingAfterOnClick()
  }

  // Close the popover
  const handleClose = () => {
    setAnchorEl(null)
  }

  // When a date is selected in the calendar
  const handleDateSelect = (date) => {
    // Convert the selected Date object to a dayjs object
    const newDayjsDate = dayjs(date)
    // Call the provided onChange callback
    onChange(newDayjsDate)
    // Close the popover
    handleClose()
  }

  // Determine if the popover is open
  const open = Boolean(anchorEl)
  const id = open ? 'dayjs-date-picker-popover' : undefined

  // Ensure we have a Date object for react-calendar
  const selectedDate = dayjsDate ? dayjs(dayjsDate).toDate() : new Date()

  // Reset calendar view when popover closes
  useEffect(() => {
    if (!open) {
      setCalendarView('month')
    }
  }, [open])

  // Filter out 'view', 'onViewChange' and 'tileClassName' from calendarProps: view/onViewChange
  // are controlled by our internal state, and tileClassName is composed below so off-day
  // flagging and any caller-supplied classes both apply.
  const {
    view: _ignoredView,
    onViewChange: _ignoredOnViewChange,
    tileClassName: externalTileClassName,
    ...safeCalendarProps
  } = calendarProps || {}

  // Off days stay selectable — only their colour changes (via the --off-day class). We add it
  // in the day (month) view only, and preserve any tileClassName the caller passed.
  const getTileClassName = (tileArgs) => {
    const classes = []
    const external =
      typeof externalTileClassName === 'function'
        ? externalTileClassName(tileArgs)
        : externalTileClassName
    if (external) {
      classes.push(external)
    }
    if (
      tileArgs?.view === 'month' &&
      tileArgs?.date instanceof Date &&
      offDaySet.has(tileArgs.date.getDay())
    ) {
      classes.push('react-calendar__tile--off-day')
    }
    return classes.length > 0 ? classes.join(' ') : null
  }

  return (
    <>
      <ButtonComponent
        disabled={isDisabled}
        variant="outlined"
        onClick={handleButtonClick}
        {...buttonProps}
      >
        {buttonTextOverride !== null 
          ? buttonTextOverride 
          : (dayjsDate ? dayjs(dayjsDate).format(format) : placeholder)}
      </ButtonComponent>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        container={() => document.body}
        disablePortal={false}
        sx={{
          zIndex: 30000,
        }}
        PaperProps={{
          style: {
            // NOTE: do not set `position` here — MUI anchors the popover with an inline
            // `position: absolute`, and overriding it detached the calendar from the button.
            backgroundColor: '#ffffff',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            overflow: 'visible',
            marginTop: '4px',
          },
          sx: {
            zIndex: 30000,
          },
        }}
        slotProps={{
          root: {
            sx: {
              zIndex: 30000,
            },
          },
        }}
      >
        <DayJsDateSelectorCalendarStyleContainer>
          <Calendar
            onChange={handleDateSelect}
            value={selectedDate}
            view={calendarView}
            onViewChange={({ view }) => setCalendarView(view)}
            tileClassName={getTileClassName}
            {...safeCalendarProps}
          />
        </DayJsDateSelectorCalendarStyleContainer>
      </Popover>
    </>
  )
}

export default DayjsDatePicker
